/* What the grid needs to know of a column, from the space's column: its width, its header
   icon, whether a cell edits in place, and how a value reads as text (copying, the summary). */
import {
  AlignLeft, AtSign, Calendar, CalendarClock, CircleDot, Hash, Link2, ListChecks, Phone, Sigma, SquareCheck, Text, UserRound,
  type LucideIcon,
} from "lucide-react";

import type { SpaceColumn, SpaceColumnType, SpaceLinked, SpaceValue } from "@/shared/types";

export const ICONS: Record<SpaceColumnType, LucideIcon> = {
  text: AlignLeft,
  long_text: Text,
  number: Hash,
  boolean: SquareCheck,
  date: Calendar,
  datetime: CalendarClock,
  choice: CircleDot,
  email: AtSign,
  url: Link2,
  phone: Phone,
  person: UserRound,
  link: ListChecks,
  count: Sigma,
};

const WIDTHS: Record<SpaceColumnType, number> = {
  text: 180, long_text: 260, number: 112, boolean: 96, date: 120, datetime: 168, choice: 150,
  email: 240, url: 180, phone: 160, person: 160, link: 170, count: 120,
};

/** Whether the row keeps the column's values: a link or a count lives beside the row, so it
    is never sorted, filtered on, or turned into a kept type (or back). */
export const keptOnRow = (column: { type: SpaceColumnType }) => column.type !== "link" && column.type !== "count";

/** Columns a cell edits in place; a link or a person is picked in the row's panel, a count
    is read. */
const IN_PLACE = new Set<SpaceColumnType>(["text", "long_text", "number", "date", "datetime", "choice", "email", "url", "phone", "boolean"]);

/** Wide enough for its type's values and for its header: the icon, the name, the rule marks,
    the sort arrow and the settings button (about 116px around the name, 7.5px a letter). */
export function widthOf(column: SpaceColumn, first: boolean): number {
  const header = 116 + column.name.length * 7.5 + column.rules.filter((rule) => rule !== "verified_only").length * 16;
  return Math.max(first ? 220 : WIDTHS[column.type], Math.ceil(header));
}

export const editsInPlace = (column: SpaceColumn) => IN_PLACE.has(column.type);

/** A cell as text: what is copied, and what an editor starts from. */
export function textOf(value: SpaceValue | undefined): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map((linked: SpaceLinked) => linked.label ?? linked.id).join(", ");
  return String(value);
}

/** A short date as the table shows it: "Oct 28" this year, "Oct 28, 2025" before. */
export function shortDate(iso: string, withTime = false): string {
  // a bare date is a day, not midnight in London: read it as the day it names, wherever you are
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const when = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const sameYear = when.getFullYear() === new Date().getFullYear();
  return when.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    hour: withTime ? "2-digit" : undefined,
    minute: withTime ? "2-digit" : undefined,
  });
}

/** The unique rules a repeat broke, in the columns' names: "Email", "Lead + Step". */
export function repeatedNames(rules: string[], columns: SpaceColumn[]): string {
  const name = (key: string) => columns.find((column) => column.key === key)?.name ?? key;
  return rules.map((rule) => rule.split("+").map(name).join(" + ")).join(", ");
}
